import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatTRY, formatNum, toNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Minus, ShoppingBag, Pencil, Trash2, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { MobileCartBar } from "@/components/sales/MobileCartBar";

const PAYMENTS = ["Nakit", "Kredi Kartı", "Karma"];

export default function Retail() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [payment, setPayment] = useState("Nakit");
  const [cash, setCash] = useState("");
  const [card, setCard] = useState("");
  const [customer, setCustomer] = useState("");
  const [discType, setDiscType] = useState("none");
  const [discVal, setDiscVal] = useState("");
  const [data, setData] = useState({ summary: null, sales: [] });
  const [priceEdit, setPriceEdit] = useState({});
  const [edit, setEdit] = useState(null);
  const [rows, setRows] = useState([]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const focusId = params.get("sale");

  const load = () => {
    api.get("/retail/products").then((r) => setProducts(r.data));
    api.get("/retail/sales").then((r) => setData(r.data));
  };
  useEffect(load, []);

  const items = useMemo(() => Object.entries(cart).map(([id, qty]) => ({ p: products.find((x) => x.id === id), qty })).filter((x) => x.p), [cart, products]);
  const subtotal = items.reduce((a, x) => a + x.qty * x.p.retail_price, 0);
  const discNum = toNum(discVal);
  const discountAmount = discType === "percent" ? subtotal * Math.min(discNum, 100) / 100 : discType === "amount" ? Math.min(discNum, subtotal) : 0;
  const total = Math.max(0, subtotal - discountAmount);
  const splitOk = payment !== "Karma" || Math.abs(toNum(cash) + toNum(card) - total) < 0.01;
  const add = (id, d = 1) => setCart((c) => { const q = (c[id] || 0) + d; const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = q; return n; });

  const savePrice = async (p) => {
    const v = toNum(priceEdit[p.id]);
    try { await api.put(`/retail/products/${p.id}/price`, { retail_price: v }); toast.success(`${p.name} paket fiyatı ${formatTRY(v)}`); setPriceEdit((e) => ({ ...e, [p.id]: undefined })); load(); }
    catch { toast.error("Fiyat kaydedilemedi"); }
  };

  const checkout = async () => {
    if (items.some((x) => !x.p.retail_price)) return toast.error("Fiyatı 0 olan ürün var — önce paket satış fiyatını girin");
    try {
      const r = await api.post("/retail/sales", { items: items.map((x) => ({ ingredient_id: x.p.id, quantity: x.qty })), payment_type: payment,
        cash_amount: payment === "Karma" ? toNum(cash) : 0, card_amount: payment === "Karma" ? toNum(card) : 0, customer_name: customer.trim(),
        discount_type: discountAmount > 0 ? discType : "none", discount_value: discountAmount > 0 ? discNum : 0 });
      toast.success(`Perakende satış tamamlandı — ${formatTRY(r.data.total)} (kâr ${formatTRY(r.data.profit)})`);
      if (r.data.stock_warnings?.length) toast.warning(`${r.data.stock_warnings.map((w) => w.name).join(", ")} stoğu eksiye düştü`, { duration: 7000 });
      setCart({}); setCustomer(""); setCash(""); setCard(""); setDiscType("none"); setDiscVal(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Satış kaydedilemedi"); }
  };

  const openEdit = (s) => { setEdit(s); setRows(s.items.map((i) => ({ name: i.product_name, quantity: String(i.quantity), unit_price: String(i.unit_price) }))); };
  const saveEdit = async () => {
    try {
      await api.put(`/retail/sales/${edit.id}`, { items: rows.map((x) => ({ quantity: Math.max(0, Math.round(toNum(x.quantity))), unit_price: toNum(x.unit_price) })) });
      toast.success("Satış güncellendi"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };
  const cancel = async (s) => {
    if (!window.confirm("Bu perakende satış iptal edilsin mi? Paketler stoğa iade edilir.")) return;
    try { await api.delete(`/sales/${s.id}`); toast.success("Satış iptal edildi, stok iade edildi"); load(); } catch { toast.error("İptal edilemedi"); }
  };


  const [grpFilter, setGrpFilter] = useState("Tümü");
  const groups = useMemo(() => Object.entries(products.reduce((g, p) => { const k = p.retail_group || "Diğer"; (g[k] = g[k] || []).push(p); return g; }, {})).sort(([a], [b]) => (a === "Diğer") - (b === "Diğer") || a.localeCompare(b, "tr")), [products]);
  const shownGroups = grpFilter === "Tümü" ? groups : groups.filter(([g]) => g === grpFilter);

  const sum = data.summary;
  return (
    <div className="space-y-6 pb-28 lg:pb-0" data-testid="retail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Perakende Satış</h1>
          <p className="mt-1 text-sm text-muted-foreground">Yalnızca fiyat listesi PDF'inden yüklenen <b>HERBALİFE</b> ürünleri paket olarak satılır — stok düşer, kazanç ayrı izlenir</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/perakende-stok")} data-testid="retail-goto-stock"><Warehouse className="mr-2 h-4 w-4" /> Herbalife Stok</Button>
        </div>
      </div>

      {sum && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="retail-summary">
          {[["Satış", `${sum.count} fiş`, `${sum.items} paket`], ["Ciro", formatTRY(sum.revenue), ""], ["Maliyet + KDV", formatTRY(sum.cost + (sum.vat || 0)), `alış ${formatTRY(sum.cost)} · dahil KDV %10 ${formatTRY(sum.vat || 0)}`], ["Perakende Net Kâr", formatTRY(sum.profit), ""]].map(([l, v, s]) => (
            <div key={l} className="rounded-xl border border-border bg-card p-4"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</p><p className="mt-1 font-heading text-2xl font-extrabold">{v}</p>{s && <p className="text-xs text-muted-foreground">{s}</p>}</div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Package className="h-5 w-5 text-primary" /> Paket Ürünler <span className="text-xs font-normal text-muted-foreground">({products.length})</span></h3>
          {products.length === 0 && <p className="mt-6 text-sm text-muted-foreground" data-testid="retail-empty">Henüz PDF'den ürün yüklenmedi. Sağ üstten fiyat listesi PDF'ini yükleyin; yalnızca PDF'deki ürünler burada satılır.</p>}
          {groups.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="retail-group-tabs">
              {[["Tümü", products.length], ...groups.map(([g, l]) => [g, l.length])].map(([g, n]) => (
                <button key={g} type="button" data-testid={`retail-group-tab-${g}`} onClick={() => setGrpFilter(g)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${grpFilter === g ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{g} <span className="opacity-70">{n}</span></button>
              ))}
            </div>
          )}
          <div className="mt-3" data-testid="retail-products">
            {shownGroups.map(([grp, list]) => (
              <div key={grp} className="mb-4">
                <p className="mb-1 border-b border-border pb-1 text-xs font-bold uppercase tracking-wide text-primary" data-testid={`retail-group-${grp}`}>{grp} <span className="font-normal text-muted-foreground">({list.length})</span></p>
                <div className="divide-y divide-border">
                {list.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5" data-testid={`retail-product-${p.id}`}>
                <div className="min-w-0 basis-full break-words lg:flex-1 lg:basis-auto">
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground">Paket {formatNum(p.package_grams, 0)} {p.unit} · stokta <b className={p.packages_in_stock <= 0 ? "text-destructive" : ""}>{p.packages_in_stock}</b> paket · maliyet {formatTRY(p.package_cost)}{p.package_vp ? ` · ${formatNum(p.package_vp, 1)} VP` : ""}
                    {p.retail_price > 0 && (() => { const m = (p.retail_price / 1.10 - p.package_cost) / p.retail_price * 100; return <span className={`ml-2 rounded px-1.5 py-0.5 font-semibold ${m < 20 ? "bg-destructive/15 text-destructive" : m < 35 ? "bg-amber-500/15 text-amber-600" : "bg-emerald-500/15 text-emerald-600"}`} title="Fiyat KDV dahil; %10 KDV ayrıldıktan sonra" data-testid={`retail-margin-${p.id}`}>%{formatNum(m, 0)} net marj</span>; })()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Input className="h-8 w-24 text-right text-sm" inputMode="decimal" placeholder="Paket ₺" data-testid={`retail-price-${p.id}`}
                      value={priceEdit[p.id] ?? String(p.retail_price || "")} onChange={(e) => setPriceEdit((x) => ({ ...x, [p.id]: e.target.value }))} />
                    {priceEdit[p.id] !== undefined && priceEdit[p.id] !== String(p.retail_price || "") && <Button size="sm" variant="secondary" className="h-8 text-xs" data-testid={`retail-price-save-${p.id}`} onClick={() => savePrice(p)}>Kaydet</Button>}
                  </div>
                  <Button className="h-11 px-4 text-sm active:scale-95 lg:h-9 lg:px-3" data-testid={`retail-add-${p.id}`} onClick={() => add(p.id)}><Plus className="mr-1 h-4 w-4" /> Sepete</Button>
                </div>
              </div>
            ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div id="retail-cart" className="scroll-mt-20 rounded-xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><ShoppingBag className="h-5 w-5" /> Sepet</h3>
          {items.length === 0 ? <p className="mt-4 text-sm text-muted-foreground" data-testid="retail-cart-empty">Sepet boş</p> : (
            <div className="mt-3 space-y-2" data-testid="retail-cart">
              {items.map((x) => (
                <div key={x.p.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`retail-cart-item-${x.p.id}`}>
                  <span className="min-w-0 flex-1 truncate">{x.p.name}</span>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="secondary" className="h-10 w-10 lg:h-6 lg:w-6" onClick={() => add(x.p.id, -1)} data-testid={`retail-dec-${x.p.id}`}><Minus className="h-4 w-4 lg:h-3 lg:w-3" /></Button>
                    <span className="w-6 text-center font-mono text-base lg:text-sm">{x.qty}</span>
                    <Button size="icon" variant="secondary" className="h-10 w-10 lg:h-6 lg:w-6" onClick={() => add(x.p.id, 1)} data-testid={`retail-inc-${x.p.id}`}><Plus className="h-4 w-4 lg:h-3 lg:w-3" /></Button>
                  </div>
                  <span className="w-20 text-right font-mono">{formatTRY(x.qty * x.p.retail_price)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 space-y-2">
            <Select value={payment} onValueChange={setPayment}><SelectTrigger data-testid="retail-payment"><SelectValue /></SelectTrigger><SelectContent>{PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            {payment === "Karma" && <div className="grid grid-cols-2 gap-2"><Input placeholder="Nakit ₺" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} data-testid="retail-cash" /><Input placeholder="Kart ₺" inputMode="decimal" value={card} onChange={(e) => setCard(e.target.value)} data-testid="retail-card" /></div>}
            <Input placeholder="Müşteri (isteğe bağlı)" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="retail-customer" />
            <div className="rounded-lg border border-border bg-background/50 p-2" data-testid="retail-discount-box">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {[["none", "Yok"], ["percent", "%"], ["amount", "₺"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`retail-discount-type-${v}`} onClick={() => setDiscType(v)}
                      className={`rounded-md px-2 py-1 text-xs font-semibold ${discType === v ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{l}</button>
                  ))}
                </div>
                <Input className="h-8 text-sm" inputMode="decimal" placeholder="İndirim" disabled={discType === "none"} value={discVal} onChange={(e) => setDiscVal(e.target.value)} data-testid="retail-discount-value" />
              </div>
            </div>
          </div>
          {discountAmount > 0 && (
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>Ara toplam</span><span className="font-mono">{formatTRY(subtotal)}</span></div>
              <div className="flex justify-between text-destructive"><span>İndirim{discType === "percent" ? ` (%${discNum})` : ""}</span><span className="font-mono" data-testid="retail-discount-amount">−{formatTRY(discountAmount)}</span></div>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between border-t border-border pt-3"><span className="text-sm text-muted-foreground">Toplam</span><span className="font-heading text-2xl font-extrabold text-primary" data-testid="retail-total">{formatTRY(total)}</span></div>
          <Button className="mt-3 h-12 w-full text-base font-semibold lg:h-10 lg:text-sm" disabled={items.length === 0 || !splitOk} onClick={checkout} data-testid="retail-checkout">Satışı Tamamla</Button>
        </div>
      </div>
      <MobileCartBar count={items.reduce((a, x) => a + x.qty, 0)} total={formatTRY(total)} onCheckout={checkout} disabled={!splitOk} cartId="retail-cart" testid="retail-mobile-bar" />

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-heading text-lg font-semibold">Son Perakende Satışlar</h3>
        <div className="mt-3 divide-y divide-border" data-testid="retail-sales">
          {data.sales.length === 0 && <p className="py-4 text-sm text-muted-foreground">Henüz perakende satış yok</p>}
          {data.sales.map((s) => (
            <div key={s.id} className={`flex items-center justify-between gap-3 py-2.5 ${s.id === focusId ? "rounded-lg bg-primary/10 px-2 ring-1 ring-primary/40" : ""}`} data-testid={`retail-sale-${s.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{s.customer_name && <span className="mr-1 rounded bg-primary/15 px-1.5 py-0.5 text-xs text-primary">{s.customer_name}</span>}{s.items.map((i) => `${i.quantity}x ${i.product_name}`).join(", ")}</p>
                <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })} · {`${s.payment_type}${s.discount_amount > 0 ? ` · indirim −${formatTRY(s.discount_amount)}` : ""} · KDV ${formatTRY(s.vat_total || 0)} · net kâr ${formatTRY(s.profit)}`}{s.created_by ? ` · ${s.created_by}` : ""}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="font-heading font-bold">{formatTRY(s.total)}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`retail-edit-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`retail-cancel-${s.id}`} onClick={() => cancel(s)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="retail-edit-dialog">
          <DialogHeader><DialogTitle>Perakende Satışı Düzenle</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Adet 0 → ürün satıştan çıkar ve stoğa iade edilir.</p>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2">
                <span className="col-span-12 truncate text-sm sm:col-span-6">{r.name}</span>
                <Input className="col-span-6 h-9 text-right text-sm sm:col-span-3 sm:h-8" inputMode="numeric" placeholder="Adet" value={r.quantity} data-testid={`retail-edit-qty-${i}`} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                <Input className="col-span-6 h-9 text-right text-sm sm:col-span-3 sm:h-8" inputMode="decimal" placeholder="Fiyat ₺" value={r.unit_price} data-testid={`retail-edit-price-${i}`} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="retail-edit-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
